import {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'

import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'

import ConfirmationController from '../admin/invitations/ConfirmationController'
import Loader from '../shared/Loader'
import RecoveryInvitation from './recovery/RecoveryInvitation'

import {connect} from '../../state/hooks/tools'
import {isEmpty} from '../../lib/tools'
import {readEndpoint} from '../../lib/asyncMethods'
import {RECOVERY, SENT} from '../../lib/recovery'
import {INVITES, TOKEN} from '../../state/actionTypes'

/**
 * Both kinds of invitation arrive on the same link, so this is where they part company.
 *
 * An account invite and a recovery invite are the same record under the same token; only the
 * invite's category tells them apart, and reading it takes a second request the recipient's browser
 * has to make before either page can be shown. Doing it here means the token is fetched once and
 * each page below is handed what it needs rather than going looking for it.
 *
 * A token that resolves to nothing lands on the same explanation whichever kind it was, because
 * without a token there is no invite to ask what kind it is.
 */
const ExpiredInvitation = ({inviterName}) => (
  <Paper elevation={3} component='div' className='expired-invitation center-layout'>
    <Typography variant='h5' gutterBottom>This invitation is no longer valid</Typography>
    <Typography variant='body1'>
      The link has already been used, or a newer one has replaced it.
      {inviterName ? ` Ask ${inviterName} to send you a new one.` : ' Ask whoever invited you to send you another.'}
    </Typography>
  </Paper>
)

const Invitation = ({invitation, token}) => {
  if (isEmpty(invitation))
    return <ExpiredInvitation />

  const {invite} = invitation

  if (invite.category !== RECOVERY)
    return <ConfirmationController token={invitation.token} />

  // a recovery invite is only answerable while it is outstanding; one already accepted or declined
  // would earn a 422 from the page below
  if (invite.status !== SENT)
    return <ExpiredInvitation inviterName={invite.inviterName} />

  return <RecoveryInvitation invite={invite} inviterName={invite.inviterName} token={token} />
}

const InvitationController = props => {
  const [invitation, setInvitation] = useState({})
  const [loaded, setLoaded] = useState(false)
  const params = useParams()

  useEffect(() => {
    props.readToken(`tokens/${params.token}`).then(response => {
      if (!response.success)
        return null

      return props.readInvite(`invites/${response.data.tokenableId}`).then(invite => {
        if (invite.success)
          setInvitation({token: response.data, invite: invite.data})
      })
    }).catch(() => setInvitation({})).finally(() => setLoaded(true))
  }, [])

  return (
    <Loader loading={!loaded}>
      <Invitation invitation={invitation} token={params.token} />
    </Loader>
  )
}

const actionCreators = ({users, invites}) => ({
  readToken: readEndpoint(users, TOKEN),
  readInvite: readEndpoint(invites, INVITES)
})

export default connect(null, actionCreators)(InvitationController)
